import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import api from '../../api/req';
import CiatAppContextProvider from '../../providers/CiatAppContext/provider';
import LoginContext from '../../providers/authLoginProvider';
import LoginRoutes from '../../components/Login/loginRoutes';

function RootChild() {
  const [currentUser, setCurrentUser] = useState(null);
  const tokens = useRef({
    accessToken: null,
    refreshToken: null,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();
  const navigateRef = useRef(null);
  navigateRef.current = navigate;
  // key-value store for all settings in current session
  const clearSession = useCallback(
    () => {
      window.localStorage.removeItem('refreshToken');
      setCurrentUser(null);
      tokens.current.accessToken = null;
      tokens.current.refreshToken = null;
    },
    [],
  );

  const tryLogout = useCallback(
    () => {
      setLoading(true);
      api.get('/api-auth/logout', () => ({ access: tokens.current.accessToken }))
        .then((r) => {
          if (r.ok) {
            clearSession();
            setLoading(false);
            setErr(null);
            localStorage.removeItem('ticket');
          } else {
            setErr(`${r.status} ${r.statusText}`);
          }
        })
        .catch((e) => setErr(e.message))
        .finally(() => setLoading(false));
    },
    [clearSession],
  );

  const tryCurrentUser = useCallback(
    async (accessToken, refreshToken, showToast = true) => {
      try {
        const resp = await api.get$('/api/auth/currentuser/', () => ({ access: accessToken }));
        if (resp.ok) {
          const d = await resp.json();
          const c = d[0];
          setCurrentUser(c);
          if (showToast) {
            const userName = (c.first_name || c.last_name) ? `${c.first_name} ${c.last_name}` : c.username;
            toast.info(`Доброго дня ${userName}. Бажаємо Вам гарного дня, та успіхів в роботі!`, {
              theme: 'colored',
            });
          }
          setLoading(false);
          tokens.current.accessToken = accessToken;
          tokens.current.refreshToken = refreshToken;
          setErr(null);
          return c;
        }
        setErr(`${resp.status} ${resp.statusText}`);
      } catch (e) {
        setErr(e.message);
      }

      setLoading(false);
      clearSession();

      return false;
    },
    [clearSession],
  );

  const tryRefresh = useCallback(
    async () => {
      // eslint-disable-next-line
      const refreshToken = tokens.current.refreshToken ||
        (process.env.NODE_ENV !== 'production' && window.localStorage.getItem('refreshToken'));
      if (refreshToken) {
        try {
          const r = await api.post$('/api/token/refresh', () => null, { refresh: refreshToken });
          if (r.ok) {
            const d = await r.json();
            window.localStorage.setItem('refreshToken', refreshToken);
            tokens.current.accessToken = d.access;
            tokens.current.refreshToken = refreshToken;
            return () => ({
              access: d.access,
            });
          }
        } catch (e) {
          setErr(e.message);
        }
      }
      tryLogout();
      return false;
    },
    [tryLogout],
  );

  const tryLogin = useCallback(
    async (access, refresh) => {
      setLoading(true);
      const cu = await tryCurrentUser(access, refresh, true);
      if (cu) {
        if (process.env.NODE_ENV !== 'production') {
          window.localStorage.setItem('refreshToken', refresh);
        }
      }
    },
    [tryCurrentUser],
  );

  useEffect(
    () => {
      setLoading(true);
      // const searchParams = new URLSearchParams(window.location.search);
      const re = /ticket=([a-z,A-Z,0-9,+,\\/]*={1,})/;
      const ticketString = window.location.href.match(re);
      if (ticketString) {
        const toDecode = ticketString[1];
        try {
          // const authdata = JSON.parse(toDecode);
          const { user, passwd } = JSON.parse(atob(toDecode));
          tryLogin(user, passwd);
        } catch (e) {
          throw new Error(`Ticket error ${e}`);
        }
      }
      tryRefresh().then(() => {
        tryCurrentUser(tokens.current.accessToken, tokens.current.refreshToken, false);
        setLoading(false);
      });
    },
    [tryCurrentUser, tryLogin, tryRefresh],
  );

  const loginsCtxValue = useMemo(() => ({
    onLogin: tryLogin,
    errorMsg: err,
    loading,
    onClearError: () => setErr(null),
  }), [err, loading, tryLogin]);
  if (!currentUser) {
    return (
      <LoginContext.Provider value={loginsCtxValue}>
        <LoginRoutes onLogin={tryLogin} />
      </LoginContext.Provider>
    );
  }
  return (
    <CiatAppContextProvider
      currentUser={currentUser}
      tokens={tokens}
      logoutHandler={tryLogout}
      refreshHandler={tryRefresh}
      currentUserRefresh={tryCurrentUser}
    >
      <Layout />
    </CiatAppContextProvider>
  );
}

export default RootChild;
